import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../style/Admin.css';

const API = process.env.REACT_APP_API_URL || '';

export default function AdminDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = ['announcements', 'testimonials'].includes(searchParams.get('tab'))
        ? searchParams.get('tab') : 'jobs';

    const [jobs, setJobs]                     = useState([]);
    const [announcements, setAnnouncements]   = useState([]);
    const [testimonials, setTestimonials]     = useState([]);
    const [loading, setLoading]               = useState(true);
    const [error, setError]                   = useState('');
    const { token, logout }                   = useAdminAuth();
    const navigate                            = useNavigate();

    const fetchJobs = async () => {
        try {
            const res  = await fetch(`${API}/api/admin/jobs`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (!res.ok) { setError('Failed to load jobs.'); return; }
            const data = await res.json();
            setJobs(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load jobs.');
        }
    };

    const fetchAnnouncements = async () => {
        try {
            const res  = await fetch(`${API}/api/admin/announcements`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (!res.ok) { setError('Failed to load announcements.'); return; }
            const data = await res.json();
            setAnnouncements(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load announcements.');
        }
    };

    const fetchTestimonials = async () => {
        try {
            const res  = await fetch(`${API}/api/admin/testimonials`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (!res.ok) { setError('Failed to load testimonials.'); return; }
            const data = await res.json();
            setTestimonials(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load testimonials.');
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchJobs(), fetchAnnouncements(), fetchTestimonials()]).finally(() => setLoading(false));
    }, []); // eslint-disable-line

    const handleDeleteJob = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`${API}/api/admin/jobs/${id}`, {
                method:  'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (res.ok) fetchJobs();
            else setError('Failed to delete job.');
        } catch {
            setError('Failed to delete job.');
        }
    };

    const handleDeleteAnnouncement = async (id, title) => {
        if (!window.confirm(`Delete announcement "${title}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`${API}/api/admin/announcements/${id}`, {
                method:  'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (res.ok) fetchAnnouncements();
            else setError('Failed to delete announcement.');
        } catch {
            setError('Failed to delete announcement.');
        }
    };

    const handleDeleteTestimonial = async (id, name) => {
        if (!window.confirm(`Delete testimonial from "${name}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`${API}/api/admin/testimonials/${id}`, {
                method:  'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (res.ok) fetchTestimonials();
            else setError('Failed to delete testimonial.');
        } catch {
            setError('Failed to delete testimonial.');
        }
    };

    const handleLogout = () => { logout(); navigate('/admin/login'); };

    const statusBadge = (status) => (
        <span className={`admin-badge ${status === 'ACTIVE' ? 'badge-active' : 'badge-draft'}`}>
            {status}
        </span>
    );

    const typeBadge = (type) => {
        const cls = type === 'ALERT' ? 'badge-alert' : type === 'PROMO' ? 'badge-promo' : 'badge-info';
        return <span className={`admin-badge ${cls}`}>{type}</span>;
    };

    return (
        <div className="admin-layout">
            <header className="admin-header">
                <div className="admin-header-left">
                    <span className="admin-header-logo">KOVAN Admin</span>
                </div>
                <div className="admin-header-right">
                    {activeTab === 'jobs' && (
                        <button className="admin-btn-primary" onClick={() => navigate('/admin/jobs/new')}>
                            + Post New Job
                        </button>
                    )}
                    {activeTab === 'announcements' && (
                        <button className="admin-btn-primary" onClick={() => navigate('/admin/announcements/new')}>
                            + New Announcement
                        </button>
                    )}
                    {activeTab === 'testimonials' && (
                        <button className="admin-btn-primary" onClick={() => navigate('/admin/testimonials/new')}>
                            + Add Testimonial
                        </button>
                    )}
                    <button className="admin-btn-outline" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
            </header>

            <main className="admin-main">
                <div className="admin-tabs">
                    <button
                        className={`admin-tab ${activeTab === 'jobs' ? 'admin-tab-active' : ''}`}
                        onClick={() => setSearchParams({ tab: 'jobs' })}
                    >
                        Job Postings
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'announcements' ? 'admin-tab-active' : ''}`}
                        onClick={() => setSearchParams({ tab: 'announcements' })}
                    >
                        Announcements
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'testimonials' ? 'admin-tab-active' : ''}`}
                        onClick={() => setSearchParams({ tab: 'testimonials' })}
                    >
                        Testimonials
                    </button>
                </div>

                {error && <div className="admin-error">{error}</div>}

                {loading ? (
                    <div className="admin-loading">Loading...</div>
                ) : activeTab === 'jobs' ? (
                    jobs.length === 0 ? (
                        <div className="admin-empty">
                            <p>No job postings yet.</p>
                            <button className="admin-btn-primary" onClick={() => navigate('/admin/jobs/new')}>
                                Post Your First Job
                            </button>
                        </div>
                    ) : (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Location</th>
                                        <th>Type</th>
                                        <th>Level</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobs.map(job => (
                                        <tr key={job.id}>
                                            <td className="job-title-cell">{job.title}</td>
                                            <td>{job.location}</td>
                                            <td>{job.employmentType}</td>
                                            <td>{job.experienceLevel}</td>
                                            <td>{statusBadge(job.status)}</td>
                                            <td className="admin-actions">
                                                <button
                                                    className="admin-btn-edit"
                                                    onClick={() => navigate(`/admin/jobs/edit/${job.id}`)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="admin-btn-delete"
                                                    onClick={() => handleDeleteJob(job.id, job.title)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : activeTab === 'announcements' ? (
                    announcements.length === 0 ? (
                        <div className="admin-empty">
                            <p>No announcements yet.</p>
                            <button className="admin-btn-primary" onClick={() => navigate('/admin/announcements/new')}>
                                Create Your First Announcement
                            </button>
                        </div>
                    ) : (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Message</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {announcements.map(a => (
                                        <tr key={a.id}>
                                            <td className="job-title-cell">{a.title}</td>
                                            <td className="announcement-msg-cell">{a.message}</td>
                                            <td>{typeBadge(a.type)}</td>
                                            <td>{statusBadge(a.status)}</td>
                                            <td className="admin-actions">
                                                <button
                                                    className="admin-btn-edit"
                                                    onClick={() => navigate(`/admin/announcements/edit/${a.id}`)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="admin-btn-delete"
                                                    onClick={() => handleDeleteAnnouncement(a.id, a.title)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : (
                    testimonials.length === 0 ? (
                        <div className="admin-empty">
                            <p>No testimonials yet.</p>
                            <button className="admin-btn-primary" onClick={() => navigate('/admin/testimonials/new')}>
                                Add Your First Testimonial
                            </button>
                        </div>
                    ) : (
                        <div className="admin-table-wrapper">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Designation</th>
                                        <th>Company</th>
                                        <th>Message</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {testimonials.map(t => (
                                        <tr key={t.id}>
                                            <td className="job-title-cell">
                                                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                                                    {t.avatarUrl && (
                                                        <img src={t.avatarUrl} alt={t.clientName}
                                                            style={{ width:32, height:32, borderRadius:'50%', objectFit:'cover' }}
                                                            onError={(e) => { e.target.style.display='none'; }}
                                                        />
                                                    )}
                                                    {t.clientName}
                                                </div>
                                            </td>
                                            <td>{t.designation}</td>
                                            <td>{t.company}</td>
                                            <td className="announcement-msg-cell">{t.message}</td>
                                            <td>{statusBadge(t.status)}</td>
                                            <td className="admin-actions">
                                                <button
                                                    className="admin-btn-edit"
                                                    onClick={() => navigate(`/admin/testimonials/edit/${t.id}`)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="admin-btn-delete"
                                                    onClick={() => handleDeleteTestimonial(t.id, t.clientName)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </main>
        </div>
    );
}
